import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Warehouse } from 'src/app/pages/apps/warehouse/warehouse';
import { Zone } from 'src/app/pages/apps/warehouse/zone/zone';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MessangeContact } from 'src/app/pages/apps/warehouse/messange-contact/messangeContact';

export interface FlowData {
  date: string;
  received: number;
  delivered: number;
}


@Injectable({
  providedIn: 'root',
})
export class WarehouseService {
  private baseUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService,
    private router: Router,
  ) { }

  /** includeInactive: también las facilities apagadas (solo Admin y dueño; pantallas Warehouse y Payroll). */
  getWarehouses(includeInactive = false) {
    return this.http.get<any>(`${this.baseUrl}/Warehouses`, includeInactive ? { params: { includeInactive: 'true' } } : {});
  }

  /** Disable / Enable una facility (no borra nada). */
  setWarehouseActive(id: number, isActive: boolean, reason?: string): Observable<{ message: string; isActive: boolean; drivers: number; unpaidDrivers: number }> {
    return this.http.post<{ message: string; isActive: boolean; drivers: number; unpaidDrivers: number }>(
      `${this.baseUrl}/Warehouses/${id}/active`, { isActive, reason: reason ?? null });
  }
  getWarehousesRsp() {
    return this.http.get<any>(`${this.baseUrl}/Warehouses/with-rsp`);
  }

  addWarehouse(warehouse: Warehouse): Observable<Warehouse> {
    return this.http.post<Warehouse>(`${this.baseUrl}/Warehouses`, warehouse);
  }

  getMetros(companyId: number) {
    return this.http.get<any>(`${this.baseUrl}/Warehouses/metros/${companyId}`);
  }

  updateWarehouse(warehouse: Warehouse): Observable<Warehouse> {
    return this.http.put<Warehouse>(
      `${this.baseUrl}/Warehouses/${warehouse.id}`,
      warehouse,
    );
  }

  // ---------------------------------------------------------------- check-in location
  /** "Find on map": busca la dirección sin guardar nada. */
  geocodeAddress(address: { street: string; city?: string; state?: string; zipCode?: string }): Observable<GeocodePreview> {
    return this.http.post<GeocodePreview>(`${this.baseUrl}/Warehouses/geocode`, address);
  }

  /** Usar la dirección guardada como lugar de check-in. */
  locateWarehouse(id: number): Observable<{ location: FacilityLocationResult }> {
    return this.http.post<{ location: FacilityLocationResult }>(`${this.baseUrl}/Warehouses/${id}/locate`, {});
  }

  getCheckInLocations(): Observable<CheckInLocations> {
    return this.http.get<CheckInLocations>(`${this.baseUrl}/Warehouses/check-in-locations`);
  }

  deleteWarehouse(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/Warehouses/${id}`);
  }

  addZone(zone: Zone): Observable<Zone> {
    return this.http.post<Zone>(`${this.baseUrl}/Zones`, zone);
  }

  getZones() {
    return this.http.get<any>(`${this.baseUrl}/Zones`);
  }
  updateZone(zone: Zone): Observable<Zone> {
    return this.http.put<Zone>(`${this.baseUrl}/Zones/${zone.id}`, zone);
  }

  deleteZones(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/Zones/${id}`);
  }

  /** Junta las zonas repetidas del warehouse (mismo código y mismo pago): rutas a la que se queda, las otras se borran. */
  mergeRepeatedZones(warehouseId: number) {
    return this.http.post<any>(`${this.baseUrl}/Zones/warehouse/${warehouseId}/merge-repeated`, {});
  }

  getZonesByWarehouse(id: number) {
    return this.http.get<any>(`${this.baseUrl}/Zones/GetZonesWarehouse/${id}`);
  }

  getMessageContact(id: number) {
    return this.http.get<any>(
      `${this.baseUrl}/WarehouseMessageTemplates/allMessageByWarehouse/${id}`,
    );
  }
  AddMessageContact(message: MessangeContact): Observable<Zone> {
    return this.http.post<Zone>(
      `${this.baseUrl}/WarehouseMessageTemplates`,
      message,
    );
  }
  updateMessageContact(message: MessangeContact): Observable<Zone> {
    return this.http.put<Zone>(
      `${this.baseUrl}/WarehouseMessageTemplates/${message.id}`,
      message,
    );
  }

  deleteMessageContact(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/WarehouseMessageTemplates/${id}`,
    );
  }

  addMetro(data: any): Observable<any> {
    return this.http.post(`${this.baseUrl}/Warehouses/metros/add`, data);
  }

  updateMetro(id: number, data: any): Observable<any> {
    return this.http.put(`${this.baseUrl}/Warehouses/metros/${id}`, data);
  }

  deleteMetro(id: number): Observable<any> {
    return this.http.delete(`${this.baseUrl}/Warehouses/Metro/${id}`);
  }


  getCompanyFlow(period: 'week' | 'month' = 'week'): Observable<FlowData[]> {
    return this.http.get<FlowData[]>(`${this.baseUrl}/Warehouses/flow?period=${period}`);
  }



  getWarehousePerformance(from: string, to: string) {
    return this.http.get<WarehousePerformance[]>(
      `${this.baseUrl}/Warehouses/performance?from=${from}&to=${to}`
    );
  }
  getZonePayRules(zoneId: number) {
    return this.http.get<any[]>(`${this.baseUrl}/zone-pay-rules?zoneId=${zoneId}&activeOnly=true`);
  }

  addZonePayRule(data: any) {
    return this.http.post(`${this.baseUrl}/zone-pay-rules`, data);
  }

  updateZonePayRule(id: number, data: any) {
    return this.http.put(`${this.baseUrl}/zone-pay-rules/${id}`, data);
  }

  deleteZonePayRule(id: number) {
    return this.http.delete(`${this.baseUrl}/zone-pay-rules/${id}`);
  }

  /** Todas las versiones de precio de una zona (también las que ya terminaron). */
  getZonePayRuleHistory(zoneId: number) {
    return this.http.get<any[]>(`${this.baseUrl}/zone-pay-rules?zoneId=${zoneId}&activeOnly=false`);
  }

  /** Cambiar el precio desde una fecha: la versión anterior sigue pagando los días de antes. */
  newZonePayRuleVersion(id: number, data: any) {
    return this.http.post(`${this.baseUrl}/zone-pay-rules/${id}/new-version`, data);
  }

  /** Primer día de la semana de pago en curso (desde cuándo aplica una regla nueva si no se elige otro). */
  getZonePayRuleDefaultStart(zoneId: number) {
    return this.http.get<{ effectiveFrom: string }>(`${this.baseUrl}/zone-pay-rules/default-start?zoneId=${zoneId}`);
  }
}
export interface WarehousePerformance {
  warehouseId: number;
  warehouse: string;
  city: string;
  volumen: number;
  delivered: number;
  stops: number;
  cantDrivers: number;
  countRoutes: number;
  onTimePercent: number;
  status: string;
}

export interface GeocodePreview {
  status: 'Found' | 'NotFound' | 'Unavailable';
  latitude?: number | null;
  longitude?: number | null;
  matchedAddress?: string | null;
  provider?: string;
  suggestedTimeZoneId?: string;
}

export interface FacilityLocationResult {
  status: 'Found' | 'NotFound' | 'Unavailable' | 'Manual' | 'Unchanged';
  latitude?: number | null;
  longitude?: number | null;
  matchedAddress?: string | null;
  locationSource?: string | null;
  radiusMeters: number;
  timeZoneId: string;
  movedMeters?: number | null;
  message?: string | null;
}

export interface CheckInFacility {
  id: number;
  company: string;
  city: string;
  state: string;
  address: string;
  zipCode?: string | null;
  facilityCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radiusMeters: number;
  locationSource?: string | null;
  locationMatchedAddress?: string | null;
  locationUpdatedAtUtc?: string | null;
  timeZoneId: string;
  timeZoneSaved: boolean;
  /** FromAddress: sale de la dirección. Manual: a mano. Old: de antes. Missing: no tiene. */
  locationStatus: 'FromAddress' | 'Manual' | 'Old' | 'Missing';
}

export interface CheckInLocations {
  facilities: CheckInFacility[];
  timeZones: { id: string; label: string }[];
}
